import React, { memo, useCallback } from 'react';
import { StyleSheet } from 'react-native';

import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Column } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { betslipPlayerSelector, useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { useOpenPlayerProfile } from '@/feature/player-profile-sheets/hooks/use-open-player-profile-sheet';
import { LobbyCarousel } from '@/feature/promohub/components/LobbyCarousel';
import { common } from '@/styles/styles';
import { ProductDocumentDataBodyLobbycomponentsSliceItem } from '@/types/prismic.generated';
import { logger } from '@/utils/logging';

import { FavouritesList } from '../components/FavoritesList';
import { FeaturedLineupSection } from '../components/FeaturedLineupSection';
import { PlayerRow } from '../components/PlayerRow';
import { PopularPlayersList } from '../components/PopularPlayersList';
import { RaceToPlaceLobbySection } from '../components/race-to-place/RaceToPlaceLobbySection';
import { LobbyComponent } from '../utils/lobbySections';
import { PickSelectionMethods, usePickSelection } from './use-pick-selection';

/**
 * Hook responsible for rendering the lobby content
 */
export const useLobbyContentRender = ({ players }: { players: PlayerWithTeamAndEvent[] }) => {
    const { makeSelection, removeSelection } = usePickSelection();
    const { openPlayerProfile } = useOpenPlayerProfile(players);

    const openPlayerProfileWrapper = useCallback(
        (playerId: string, eventId: string) => {
            openPlayerProfile({ playerId, eventId });
        },
        [openPlayerProfile]
    );

    const renderLobbyPlayerItem = useCallback(
        ({ item, index, title }: { item: PlayerWithTeamAndEvent; title: string; index: number }) => {
            const isFirstItem = players.findIndex(it => it.id === item.id && it.event.id === item.event.id) === 0;
            const isLastItem =
                players.findIndex(it => it.id === item.id && it.event.id === item.event.id) === players.length - 1;
            return (
                <>
                    {isFirstItem ? <Box style={styles.topSeparator} /> : null}
                    <Column testID={`trendingPlayer-${index}`} paddingHorizontal={'s16'}>
                        {isFirstItem ? (
                            <Box paddingTop={'s16'}>
                                <Text variant="headlineMedium" style={styles.title}>
                                    {title}
                                </Text>
                                <SizedBox value={8} />
                            </Box>
                        ) : null}
                        <MemoPlayerRow
                            player={item}
                            makeSelection={makeSelection}
                            removeSelection={removeSelection}
                            renderBottomSeparator={!isLastItem}
                            openPlayerProfile={openPlayerProfileWrapper}
                        />
                    </Column>
                </>
            );
        },
        [makeSelection, openPlayerProfileWrapper, players, removeSelection]
    );

    const renderLobbyComponent = useCallback((component: ProductDocumentDataBodyLobbycomponentsSliceItem) => {
        switch (component.orderingkey) {
            case LobbyComponent.PopularPlayers:
                return <PopularPlayersList title={component.header as string} />;
            case LobbyComponent.PromoCarousel:
                return <LobbyCarousel style={styles.promoCarousel} />;
            case LobbyComponent.FeaturedLineups:
                return <FeaturedLineupSection title={component.header as string} />;
            case LobbyComponent.FavoritesPlayers:
                return <FavouritesList title={component.header as string} />;
            case LobbyComponent.RaceToPlace:
                return <RaceToPlaceLobbySection title={component.header as string} />;
            default:
                if (component.orderingkey && ['leagues', 'trending_players'].includes(component.orderingkey)) {
                    //Leagues can not be ordered anymore, as they stick under search always
                    //Trending players can not be ordered anymore, will always be last in the list
                } else {
                    logger.debug(`Unrecognized lobby component ordering key: ${component.orderingkey}`);
                }
                return null;
        }
    }, []);

    return {
        renderLobbyPlayerItem,
        renderLobbyComponent,
    };
};

export const MemoPlayerRow = memo(
    ({
        renderBottomSeparator,
        player,
        removeSelection,
        makeSelection,
        openPlayerProfile,
    }: {
        player: PlayerWithTeamAndEvent;
        renderBottomSeparator: boolean;
        openPlayerProfile: (playerId: string, eventId: string) => void;
    } & PickSelectionMethods) => {
        const { event, ...playerWithoutEvent } = player;

        const playerPickedEntry = useBetslipStore(
            betslipPlayerSelector({
                eventId: player.event.id,
                playerId: player.id,
            })
        );
        return (
            <Box>
                {playerPickedEntry ? (
                    <PlayerRow
                        player={playerWithoutEvent}
                        mode="selection"
                        outcome={playerPickedEntry.outcome}
                        projection={playerPickedEntry.projection}
                        event={event}
                        pressable={true}
                        analyticsTag={'trendingPlayers'}
                        removeSelection={removeSelection}
                        makeSelection={makeSelection}
                        openPlayerProfile={openPlayerProfile}
                        testID={`lobbyplayer-${player.id}-selected`}
                    />
                ) : (
                    <PlayerRow
                        player={playerWithoutEvent}
                        mode="all"
                        event={event}
                        pressable={true}
                        analyticsTag={'trendingPlayers'}
                        removeSelection={removeSelection}
                        makeSelection={makeSelection}
                        openPlayerProfile={openPlayerProfile}
                        testID={`lobbyplayer-${player.id}-unselected`}
                    />
                )}
                {renderBottomSeparator ? <LineSeparator style={common.separator} /> : null}
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    title: {
        fontSize: 22,
    },
    promoCarousel: {
        paddingTop: 8,
        paddingBottom: 24,
    },
    topSeparator: {
        height: 1,
        backgroundColor: ds.colorValue.border.strong,
    },
});
